import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Icon } from '@/components/ui/icon'
import { DialogRoot, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { settingsSections, type SettingsSection } from '@/stores/useSettingsStore'

interface SettingsLayoutProps {
  children: ReactNode
  activeSection: SettingsSection
  onSectionChange: (section: SettingsSection) => void
  onClose: () => void
}

const SECTION_LABELS: Record<SettingsSection, string> = {
  profile: 'Profile',
  invites: 'Invites',
  billing: 'Billing',
  security: 'Security',
  developer: 'Developer',
}

/** Only the sections the store exposes — hidden ones never get a tab. */
const menuItems: { id: SettingsSection; label: string }[] = settingsSections.map((id) => ({
  id,
  label: SECTION_LABELS[id],
}))

export function SettingsLayout({
  children,
  activeSection,
  onSectionChange,
  onClose,
}: SettingsLayoutProps) {
  return (
    <DialogRoot
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[90dvh] w-[calc(100%-2rem)] max-w-4xl flex-col gap-0 overflow-hidden rounded-2xl bg-card p-0"
      >
        <div className="flex h-[50px] shrink-0 items-center justify-between border-b border-border px-6">
          <DialogTitle>Settings</DialogTitle>
          <DialogDescription className="sr-only">
            Manage your account, billing, and preferences.
          </DialogDescription>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            onClick={onClose}
            aria-label="Close settings"
          >
            <Icon name="x" className="h-5 w-5" />
          </button>
        </div>
        <nav
          aria-label="Settings sections"
          className="shrink-0 overflow-x-auto border-b border-border px-6"
        >
          <ul className="flex items-center gap-6 sm:gap-8">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  aria-current={activeSection === item.id ? 'page' : undefined}
                  onClick={() => onSectionChange(item.id)}
                  className={cn(
                    'relative py-4 text-sm font-medium transition-colors',
                    activeSection === item.id
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  {item.label}
                  {activeSection === item.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-foreground" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </nav>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </DialogContent>
    </DialogRoot>
  )
}
